import React, { useState, useRef, useEffect } from "react";
import { apiDonationsReport, apiDonationsArchive, CATEGORY_COLOR } from "./api";
import { buildCSV, downloadCSV } from "../analytics/_utils";
import { CustomIcon } from "../shared/CustomIcon";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

// ============================================================
// ReportsTab — on-demand custom date-range report + PDF export.
// Accessible to SA / LA / Dept Leader / Dept member.
// Archive section visible to SA / LA / Dept Leader only.
// ============================================================
export default function ReportsTab({ currentUser, isSuperAdmin, isLimitedAdmin, isDeptLeader }) {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [nameMode, setNameMode] = useState("anonymous");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [archive, setArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveMsg, setArchiveMsg] = useState("");
  const previewRef = useRef(null);

  const canSeeArchive = !!(isSuperAdmin || isLimitedAdmin || isDeptLeader);

  useEffect(() => {
    if (!canSeeArchive) return;
    (async () => {
      const a = await apiDonationsArchive();
      setArchive(a === null ? [] : (a || []));
    })();
  }, [canSeeArchive]);

  const generate = async () => {
    setError(""); setLoading(true); setReport(null);
    try {
      const data = await apiDonationsReport({ from, to, nameMode });
      setReport(data);
    } catch (e) {
      setError(e.message === "403" ? "You don't have permission to generate reports." : "Could not generate report.");
    }
    setLoading(false);
  };

  const downloadPdf = async () => {
    if (!report || !previewRef.current) return;
    setDownloading(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(previewRef.current, { backgroundColor: "var(--kc-surface)", scale: 2, useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      pdf.setFillColor(26, 26, 26); pdf.rect(0, 0, 210, 297, "F");
      const margin = 12, maxW = 210 - margin * 2, maxH = 297 - margin * 2;
      const ratio = canvas.width / canvas.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      pdf.addImage(img, "PNG", (210 - w) / 2, (297 - h) / 2, w, h, undefined, "FAST");
      pdf.save(`Kingdom-Connect-Donations-Report-${from}_to_${to}.pdf`);
    } catch (e) {
      setError("PDF export failed. Please try Print instead.");
    }
    setDownloading(false);
  };

  return (
    <div data-testid="donations-reports-tab">
      <style>{REPORTS_CSS}</style>
      <div className="don-toolbar">
        <div className="don-toolbar-filters">
          <label><span className="don-filter-label">From</span><input type="date" className="don-filter-select" data-testid="rep-from" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label><span className="don-filter-label">To</span><input type="date" className="don-filter-select" data-testid="rep-to" value={to} onChange={e => setTo(e.target.value)} /></label>
          <label><span className="don-filter-label">Names</span>
            <select className="don-filter-select" data-testid="rep-namemode" value={nameMode} onChange={e => setNameMode(e.target.value)}>
              <option value="anonymous">Anonymous</option>
              <option value="initials">Initials only</option>
              <option value="full">Full names</option>
            </select>
          </label>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-gold" data-testid="rep-generate" onClick={generate} disabled={loading}>{loading ? "Generating…" : "Generate Report"}</button>
          {isSuperAdmin && (
            <button
              type="button"
              className="btn btn-outline"
              data-testid="rep-generate-archive-now"
              disabled={archiving}
              onClick={async () => {
                setArchiveMsg("");
                const year = new Date().getFullYear() - 1;
                const yes = window.confirm(`Generate the year-end archive for ${year}? This will create or overwrite the saved snapshot in the Reports Archive.`);
                if (!yes) return;
                setArchiving(true);
                try {
                  const res = await kcFetch(`${API_BASE}/donations/reports/archive/generate-now`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({ year }),
                  });
                  if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.detail || "Could not generate archive.");
                  }
                  const j = await res.json();
                  setArchiveMsg(`\u2713 ${j.report?.year} archive generated — ${j.report?.totalCount} intakes, $${(j.report?.totalValue ?? 0).toFixed(2)} total.`);
                  const a = await apiDonationsArchive();
                  setArchive(a === null ? [] : (a || []));
                } catch (e) {
                  setArchiveMsg(`! ${e.message || "Could not generate archive."}`);
                } finally {
                  setArchiving(false);
                }
              }}
            >{archiving ? "Archiving…" : (<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="folder" size={14} /> Generate Year-End Archive Now</span>)}</button>
          )}
          {report && <button className="btn btn-outline" data-testid="rep-download-pdf" onClick={downloadPdf} disabled={downloading}>{downloading ? "Preparing…" : "⬇ Download PDF"}</button>}
          {report && (
            <span
              data-testid="rep-intake-count"
              title={report.intakes && report.intakes.length === 0 ? "No intakes in this date range — CSV will be empty." : `${report.intakes.length} intake${report.intakes.length === 1 ? "" : "s"} in CSV`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 10px",
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.4,
                borderRadius: 999,
                background: (report.intakes && report.intakes.length > 0) ? "rgba(74,222,128,0.10)" : "rgba(255,155,107,0.10)",
                border: `1px solid ${(report.intakes && report.intakes.length > 0) ? "rgba(74,222,128,0.40)" : "rgba(255,155,107,0.40)"}`,
                color: (report.intakes && report.intakes.length > 0) ? "#4ade80" : "#ff9b6b",
              }}
            >
              {(report.intakes && report.intakes.length) || 0} {((report.intakes && report.intakes.length) === 1) ? "intake" : "intakes"}
            </span>
          )}
          {report && (
            <button
              type="button"
              className="btn btn-outline"
              data-testid="rep-download-csv"
              onClick={() => {
                // The backend response shape is {summary, byCategory, byFund,
                // intakes, distributions, fulfilledNeedsList}. Each intake has
                // {dateReceived, donorDisplay, category, fund, itemName,
                // estimatedValue}. Map those onto the user-facing CSV columns.
                const cleanValue = (v) => {
                  if (v == null || v === "") return "";
                  const num = Number(String(v).replace(/[^\d.-]/g, ""));
                  return Number.isFinite(num) ? num.toFixed(2) : String(v);
                };
                const headers = ["Date", "Donor", "Category", "Fund", "Items / Notes", "Value"];
                const rows = (report.intakes || []).map(i => [
                  i.dateReceived || "",
                  i.donorDisplay || "Anonymous",
                  i.category || "",
                  i.fund || "",
                  i.itemName || i.notes || "",
                  cleanValue(i.estimatedValue),
                ]);
                downloadCSV(`donations-${from}_to_${to}.csv`, buildCSV({ headers, rows }));
              }}
            >⬇ Download CSV</button>
          )}
        </div>
      </div>
      {error && <div className="don-empty" data-testid="rep-error" style={{ color: "#ff9b9b", borderColor: "rgba(255,107,107,0.40)" }}>{error}</div>}
      {archiveMsg && (
        <div data-testid="rep-archive-msg" style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, fontSize: 13, background: archiveMsg.startsWith("\u2713") ? "rgba(74,222,128,0.10)" : "rgba(255,107,107,0.10)", border: `1px solid ${archiveMsg.startsWith("\u2713") ? "rgba(74,222,128,0.40)" : "rgba(255,107,107,0.40)"}`, color: archiveMsg.startsWith("\u2713") ? "#4ade80" : "#ff9b9b" }}>
          {archiveMsg}
        </div>
      )}

      {report && <ReportPreview ref={previewRef} report={report} />}

      {canSeeArchive && (
        <section className="reports-archive" data-testid="reports-archive">
          <h3 className="reports-archive-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="folder" size={18} /> Reports Archive</h3>
          {(archive || []).length === 0 ? (
            <div className="don-empty">No archived reports yet. End-of-year reports are auto-generated on the first server start of the new year.</div>
          ) : (
            <div className="don-list">
              {(archive || []).map(r => (
                <div key={r.id} className="don-row" data-testid={`archive-row-${r.id}`}>
                  <div className="don-row-icon" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}><CustomIcon name="chart" size={18} /></div>
                  <div className="don-row-main">
                    <div className="don-row-title">{r.year} End-of-Year Donations Summary</div>
                    <div className="don-row-meta">
                      <span>{r.totalCount} donations</span>
                      <span>≈ ${Number(r.totalValue || 0).toFixed(2)}</span>
                      <span>Generated {new Date(r.generatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="don-row-chips">
                    <button className="btn btn-sm btn-outline" onClick={() => { setFrom(r.range.from); setTo(r.range.to); }}>Reload</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

const ReportPreview = React.forwardRef(({ report }, ref) => {
  const s = report.summary || {};
  const cat = report.byCategory || {};
  const fund = report.byFund || {};
  return (
    <div ref={ref} className="report-preview" data-testid="rep-preview">
      <div className="rp-head">
        <div className="rp-crown" aria-hidden="true"><CustomIcon name="crown" size={28} /></div>
        <div>
          <div className="rp-brand">Kingdom Connect</div>
          <div className="rp-tag">Donations Report</div>
        </div>
      </div>
      <div className="rp-range">{report.range.from || "—"} → {report.range.to || "—"}</div>

      <div className="rp-stats">
        <Stat n={s.totalCount || 0} label="Donations received" />
        <Stat n={`$${(s.totalValue || 0).toFixed(2)}`} label="Total est. value" />
        <Stat n={s.distributed || 0} label="Items distributed" />
        <Stat n={s.fulfilledNeeds || 0} label="Needs fulfilled" />
      </div>

      <ReportSection title="By Category" data={cat} colorMap={CATEGORY_COLOR} testid="rep-by-category" />
      <ReportSection title="By Fund"     data={fund} colorMap={null}            testid="rep-by-fund" />

      {report.intakes && report.intakes.length > 0 && (
        <div className="rp-list-section">
          <div className="rp-section-title">Intake Entries</div>
          <table className="rp-table">
            <thead><tr><th>Date</th><th>Item</th><th>Category</th><th>Fund</th><th>Donor</th><th>Value</th></tr></thead>
            <tbody>
              {report.intakes.map(i => (
                <tr key={i.id}><td>{i.dateReceived}</td><td>{i.itemName}</td><td>{i.category}</td><td>{i.fund}</td><td>{i.donorDisplay}</td><td>{i.estimatedValue || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {report.distributions && report.distributions.length > 0 && (
        <div className="rp-list-section">
          <div className="rp-section-title">Distributions</div>
          <table className="rp-table">
            <thead><tr><th>Date</th><th>Item</th><th>Status</th><th>Recipient</th></tr></thead>
            <tbody>
              {report.distributions.map(d => (
                <tr key={d.id}><td>{d.distributionDate}</td><td>{d.itemName}</td><td>{d.status}</td><td>{d.recipientDisplay}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {report.fulfilledNeedsList && report.fulfilledNeedsList.length > 0 && (
        <div className="rp-list-section">
          <div className="rp-section-title">Fulfilled Community Needs</div>
          <table className="rp-table">
            <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Urgency</th></tr></thead>
            <tbody>
              {report.fulfilledNeedsList.map(n => (
                <tr key={n.id}><td>{n.dateAdded}</td><td>{n.description}</td><td>{n.category}</td><td>{n.urgency}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

function Stat({ n, label }) {
  return (
    <div className="rp-stat">
      <div className="rp-stat-num">{n}</div>
      <div className="rp-stat-label">{label}</div>
    </div>
  );
}
function ReportSection({ title, data, colorMap, testid }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return null;
  return (
    <div className="rp-bd-section" data-testid={testid}>
      <div className="rp-section-title">{title}</div>
      <div className="rp-bd-rows">
        {entries.map(([k, v]) => {
          const color = (colorMap && colorMap[k]) || "var(--kc-accent)";
          return (
            <div key={k} className="rp-bd-row">
              <span className="rp-bd-chip" style={{ background: `${color}1F`, color }}>{k}</span>
              <span className="rp-bd-count">{v.count}</span>
              <span className="rp-bd-value">${Number(v.value || 0).toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const REPORTS_CSS = `
.report-preview {
  background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
  border-radius: 14px; padding: 28px; margin-top: 16px; color: var(--kc-text);
}
.rp-head { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; }
.rp-crown { font-size: 36px; }
.rp-brand { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.rp-tag { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-accent); letter-spacing: 0.10em; text-transform: uppercase; }
.rp-range { font-family: 'Montserrat',sans-serif; font-size: 12px; color: var(--kc-sub); margin-bottom: 22px; padding-bottom: 12px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); }
.rp-stats { display: grid; gap: 12px; grid-template-columns: repeat(4, 1fr); margin-bottom: 22px; }
@media (max-width: 640px) { .rp-stats { grid-template-columns: repeat(2, 1fr); } }
.rp-stat { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 10px; padding: 14px; text-align: center; }
.rp-stat-num { font-family: 'Cinzel',serif; font-size: 22px; font-weight: 700; color: var(--kc-accent); line-height: 1; }
.rp-stat-label { font-family: 'Montserrat',sans-serif; font-size: 10px; color: var(--kc-sub); margin-top: 6px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
.rp-bd-section { margin-bottom: 22px; }
.rp-section-title { font-family: 'Cinzel',serif; font-size: 13px; font-weight: 700; color: var(--kc-accent); letter-spacing: 0.10em; text-transform: uppercase; margin-bottom: 10px; }
.rp-bd-rows { display: flex; flex-direction: column; gap: 6px; }
.rp-bd-row { background: rgba(255,255,255,0.03); border-radius: 8px; padding: 9px 12px; display: grid; grid-template-columns: 1fr auto auto; gap: 14px; align-items: center; font-family: 'Montserrat',sans-serif; font-size: 12px; }
.rp-bd-chip { padding: 3px 9px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.rp-bd-count { font-family: 'Cinzel',serif; font-weight: 700; color: var(--kc-text); }
.rp-bd-value { font-family: 'Cinzel',serif; font-weight: 700; color: var(--kc-accent); }
.rp-list-section { margin-top: 20px; }
.rp-table { width: 100%; border-collapse: collapse; font-family: 'Montserrat',sans-serif; font-size: 12px; }
.rp-table th { text-align: left; padding: 8px 10px; font-size: 10px; font-weight: 700; color: var(--kc-sub); letter-spacing: 0.06em; text-transform: uppercase; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); }
.rp-table td { padding: 8px 10px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.reports-archive { margin-top: 36px; padding-top: 22px; border-top: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); }
.reports-archive-title { font-family: 'Cinzel',serif; font-size: 16px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.06em; text-transform: uppercase; margin: 0 0 14px; }
`;
